<template>
  <el-card shadow="never" class="filters-card" :body-style="{ padding: '14px' }">
    <template #header>
      <div class="filters-header">
        <el-icon><Filter /></el-icon>
        <span>Фильтры</span>
      </div>
    </template>

    <!-- Search -->
    <div class="filter-item">
      <label>Поиск</label>
      <el-input
        v-model="postsStore.searchQuery"
        placeholder="Искать в постах..."
        clearable
        @keyup.enter="applyFilters"
      >
        <template #prefix>
          <el-icon><Search /></el-icon>
        </template>
      </el-input>
    </div>

    <!-- Status -->
    <div class="filter-item">
      <label>Статус</label>
      <el-select v-model="postsStore.statusFilter" placeholder="Все статусы" clearable @change="applyFilters">
        <el-option label="Все" value="All" />
        <el-option label="На проверке" value="FLAGGED" />
        <el-option label="Подтверждён" value="VALIDATED" />
        <el-option label="Архив" value="ARCHIVED" />
        <el-option label="Новый" value="NEW" />
        <el-option label="В отчёте" value="IN_REPORT" />
      </el-select>
    </div>

    <!-- Category -->
    <div class="filter-item">
      <label>Категория</label>
      <el-select v-model="postsStore.categoryFilter" placeholder="Все категории" clearable @change="applyFilters">
        <el-option label="Все" value="All" />
        <el-option
          v-for="cat in sourcesStore.sourceNames"
          :key="cat"
          :label="cat"
          :value="cat"
        />
      </el-select>
    </div>

    <!-- Source -->
    <div class="filter-item">
      <label>Источник</label>
      <el-select v-model="postsStore.sourceFilter" placeholder="Все источники" clearable @change="applyFilters" v-loading="sourcesStore.loading">
        <el-option label="Все" value="All" />
        <el-option
          v-for="src in sourcesStore.sources"
          :key="src.external_id"
          :label="src.display_name"
          :value="src.external_id"
        />
      </el-select>
    </div>

    <!-- Federal District -->
    <div class="filter-item">
      <label>ФО</label>
      <el-select v-model="postsStore.districtFilter" placeholder="Все округа" clearable @change="applyFilters">
        <el-option label="Все" value="All" />
        <el-option label="ЦФО" value="ЦФО" />
        <el-option label="СЗФО" value="СЗФО" />
        <el-option label="ЮФО" value="ЮФО" />
        <el-option label="СКФО" value="СКФО" />
        <el-option label="ПФО" value="ПФО" />
        <el-option label="УрФО" value="УрФО" />
        <el-option label="СФО" value="СФО" />
        <el-option label="ДФО" value="ДФО" />
        <el-option label="Неопределённый" value="НЕОПР" />
      </el-select>
    </div>

    <!-- Date Range -->
    <div class="filter-item">
      <label>Дата от</label>
      <el-date-picker
        v-model="postsStore.dateFrom"
        type="date"
        placeholder="От"
        format="YYYY-MM-DD"
        value-format="YYYY-MM-DD"
        style="width: 100%"
        @change="applyFilters"
      />
    </div>

    <div class="filter-item">
      <label>Дата до</label>
      <el-date-picker
        v-model="postsStore.dateTo"
        type="date"
        placeholder="До"
        format="YYYY-MM-DD"
        value-format="YYYY-MM-DD"
        style="width: 100%"
        @change="applyFilters"
      />
    </div>

    <!-- Actions -->
    <div class="filter-actions">
      <el-button type="primary" @click="applyFilters" :icon="Search">Применить</el-button>
      <el-button @click="clearFilters" :icon="Delete">Сбросить</el-button>
    </div>

    <el-divider />

    <!-- Validator Stats -->
    <div class="validator-stats">
      <h4>👤 Статистика валидатора</h4>
      <div v-if="statsStore.validationStats" class="stats-grid">
        <div class="stat-item">
          <div class="stat-label">Проверено</div>
          <div class="stat-value">{{ statsStore.validationStats.total_validations }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">Жалобы</div>
          <div class="stat-value success">{{ statsStore.validationStats.complaint_count }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">Ложные</div>
          <div class="stat-value danger">{{ statsStore.validationStats.false_pos_count }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-label">Согласие ИИ</div>
          <div class="stat-value">{{ (statsStore.validationStats.agreement_rate * 100).toFixed(1) }}%</div>
        </div>
      </div>
      <el-empty v-else description="Нет данных" :image-size="60" />
    </div>
  </el-card>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { Search, Delete, Filter } from '@element-plus/icons-vue'
import { usePostsStore } from '@/stores/posts'
import { useSourcesStore } from '@/stores/sources'
import { useStatsStore } from '@/stores/stats'

const postsStore = usePostsStore()
const sourcesStore = useSourcesStore()
const statsStore = useStatsStore()

function applyFilters() {
  postsStore.page = 0
  postsStore.loadPosts(true)
}

function clearFilters() {
  postsStore.clearFilters()
  postsStore.page = 0
  postsStore.loadPosts(true)
}

onMounted(() => {
  sourcesStore.loadSources()
  statsStore.loadValidationStats()
})
</script>

<style scoped>
.filters-card {
  position: sticky;
  top: 72px;
}

.filters-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: var(--app-text-primary);
}

.filter-item {
  margin-bottom: 14px;
}

.filter-item label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: var(--app-text-regular);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-actions {
  display: flex;
  gap: 8px;
  margin-top: 6px;
}

.validator-stats {
  margin-top: 6px;
}

.validator-stats h4 {
  font-size: 14px;
  font-weight: 700;
  color: var(--app-text-regular);
  margin-bottom: 12px;
}

.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.stat-item {
  text-align: center;
  padding: 8px;
  background: var(--app-bg-muted);
  border-radius: 6px;
}

.stat-label {
  font-size: 11px;
  color: var(--app-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  font-weight: 700;
}

.stat-value {
  font-size: 20px;
  font-weight: 800;
  color: var(--app-text-primary);
}

.stat-value.success {
  color: #67c23a;
}

.stat-value.danger {
  color: #f56c6c;
}
</style>
